import MediaImage from "@/components/ui/MediaImage";
import { CaseData, PropertyCardProps } from "@/cms/models";
import {
  formatNumber,
  getBalconyTaxon,
  getCaseClassification,
  getCaseUrl,
  getLabel,
  isAgricultural,
  isAndels,
  isApartment,
  isCommercial,
  isCountryEstate,
  isHomeSale,
  isHouse,
  isInvestmentProperty,
  isLand,
  isLejeBolig,
  isOwnProperty,
  isRental,
  squareMetersToHectares,
} from "@/utils/helper";
import Link from "next/link";
import PropertyLabel from "./ui/PropertyLabel";
import { useState } from "react";
import { SpinnerSvg } from "./icons/Spinner";
import { EnergyLabelRange } from "./ui/EnergyLabel";
import AddressComponent from "./ui/AddressComponent";

const PropertyListCard: React.FC<
  PropertyCardProps & {
    /** In a look-system card: its photo zooms by the section's motion level (src/styles/motion.css), not on its own. */
    cardPhoto?: boolean;
  }
> = ({ property, cardPhoto = false }) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const classifications = getCaseClassification(property);

  const imageUrl = property.images.find((x) => x.mdUrl)?.mdUrl;
  const label = getLabel(property);

  return (
    <Link
      onClick={() => setIsLoading(true)}
      href={getCaseUrl(property)}
      className="flex bg-white overflow-hidden relative "
    >
      {isLoading && (
        <div className="absolute flex justify-center items-center inset-0 bg-gray-800 opacity-50 transition-opacity z-20">
          <SpinnerSvg />
        </div>
      )}

      <div className="flex flex-1 w-full">
        <div className="w-2/5 lg:w-1/4 relative overflow-hidden bg-gray-200">
          {imageUrl && (
            <MediaImage
              src={imageUrl}
              alt={property.advertisementAddressOrDisplayName}
              className={
                cardPhoto
                  ? "object-cover h-full w-full"
                  : "object-cover h-full w-full hover:scale-110 duration-300 transform"
              }
              data-re-card-photo={cardPhoto ? "" : undefined}
            />
          )}
          {label && (
            <PropertyLabel
              legacy={
                <div
                  className="absolute max-w-full truncate homepage-text-sm top-0 left-0 uppercase px-4 py-2 text-xs
              bg-[var(--button-bg-color)] text-[var(--button-text-color)] 
              hover:bg-[var(--button-hover-bg-color)] hover:text-[var(--button-hover-text-color)]"
                >
                  <span>{label}</span>
                </div>
              }
            >
              {label}
            </PropertyLabel>
          )}
        </div>
        <div className="flex w-3/5 lg:w-3/4 flex-col p-2 lg:p-4 bg-white">
          <div className="flex flex-col justify-between flex-1 gap-2 homepage-text-sm">
            <div className="flex justify-between w-full">
              <AddressComponent property={property} />
              <div className="hidden lg:flex homepage-text-lg gap-2">
                <span>{property.priceInfo}</span>
              </div>
            </div>
            <div className="homepage-text-sm">
              {getPropertyFactsView(property, classifications)}
            </div>
            <div className="flex lg:hidden  justify-between homepage-text-lg">
              <span>{property.priceInfo}</span>
            </div>
          </div>
        </div>
      </div>
    </Link>
  );
};

export default PropertyListCard;

interface HousePropertyFactProps {
  property: CaseData;
}

//const hasBalcony = ["dk.realequity.taxonomy.markeringerpaaejendom.altan"];

// https://miro.com/app/board/uXjVMunWIfg=/?moveToWidget=3458764614245107860&cot=14

function getPropertyFactsView(property: CaseData, list: string[]) {
  // Erhverv | Investering
  if (isCommercial(list) && isInvestmentProperty(list)) {
    return <CommercialInvestmentPropertyFacts property={property} />;
  }

  // Erhverv | Eget brug (Køb)
  if (isHomeSale(list) && isCommercial(list) && isOwnProperty(list)) {
    return <CommercialOwnUseBoughtPropertyFacts property={property} />;
  }

  // Erhverv | Eget brug (Leje)
  if (isRental(list) && isCommercial(list) && isOwnProperty(list)) {
    return <CommercialOwnUseRentalPropertyFacts property={property} />;
  }

  // Grunde
  if (isLand(list)) {
    return <PlotOfLandPropertyFacts property={property} />;
  }

  // Andelsboliger
  if (isAndels(list)) {
    return <AndelsPropertyFacts property={property} />;
  }

  // Lejebolig
  if (isLejeBolig(list)) {
    return <RentalPropertyFacts property={property} />;
  }

  // Ejerlejligheder, Villalejlighed
  if (isApartment(list)) {
    return <ApartmentPropertyFacts property={property} />;
  }

  // Villaer, Rækkehuse, Fritidshuse
  if (isHouse(list)) {
    return <HousePropertyFacts property={property} />;
  }

  // Landejendomme
  // Landbrug | Lystejendom
  if (isAgricultural(list) || isCountryEstate(list)) {
    return <AgriculturalPropertyFacts property={property} />;
  }

  if (isCommercial(list)) {
    return <CommercialOwnUseBoughtPropertyFacts property={property} />;
  }

  // Fallback
  return <HousePropertyFacts property={property} />;
}

// Villaer, Rækkehuse, Fritidshuse
const HousePropertyFacts: React.FC<HousePropertyFactProps> = ({ property }) => {
  return (
    <div className="" data-component-type="property-list-card">
      <div className="flex  justify-between ">
        <NumericPropertyFact
          label="Ejerudgifter (md.)"
          value={property.ownerExpensesTotalMonthly}
        />
        <BuiltYear
          yearBuilt={property.yearBuilt}
          yearRenovated={property.yearRenovationAddition}
        ></BuiltYear>
      </div>
      <div className="hidden lg:flex  justify-between ">
        <NumericPropertyFact
          label="Boligareal"
          value={property.housingArea}
          isArea={true}
        />
        <EnergyLabelFact
          energyLabel={property.energyLabel}
          energyLabelBest={property.energyLabelBest}
          energyLabelWorst={property.energyLabelWorst}
        />
      </div>
      <div className="flex justify-between ">
        <NumericPropertyFact label="Værelser" value={property.numberOfRooms} />
        <div className="hidden lg:flex ">
          <PropertyFact label="Type">{property.estateType}</PropertyFact>
        </div>
      </div>
      <div className="hidden lg:flex  justify-between ">
        {property.plotArea > 0 ? (
          <NumericPropertyFact
            label="Grundareal"
            value={property.plotArea}
            isArea={true}
          />
        ) : (
          <div className="flex h-6 w-80 justify-between"></div>
        )}
        <PropertyFact label="Sagsnr.">{property.caseNumber}</PropertyFact>
      </div>
    </div>
  );
};

// Ejerlejligheder, Villalejlighed
const ApartmentPropertyFacts: React.FC<HousePropertyFactProps> = ({
  property,
}) => {
  const balconyTaxon = getBalconyTaxon(property);

  return (
    <div className="">
      <div className="flex  justify-between ">
        <NumericPropertyFact
          label="Ejerudgifter (md.)"
          value={property.ownerExpensesTotalMonthly}
        />
        <BuiltYear
          yearBuilt={property.yearBuilt}
          yearRenovated={property.yearRenovationAddition}
        ></BuiltYear>
      </div>
      <div className="hidden lg:flex  justify-between ">
        <NumericPropertyFact
          label="Boligareal"
          value={property.housingArea}
          isArea={true}
        />
        <EnergyLabelFact
          energyLabel={property.energyLabel}
          energyLabelBest={property.energyLabelBest}
          energyLabelWorst={property.energyLabelWorst}
        />
      </div>
      <div className="hidden lg:flex  justify-between ">
        <PropertyFact label="Altan">
          <span>
            {balconyTaxon ? (
              balconyTaxon.note ? (
                <span>{balconyTaxon.note?.substring(0, 25)}</span>
              ) : (
                <span>Ja</span>
              )
            ) : (
              <span>Nej</span>
            )}
          </span>
        </PropertyFact>
        <PropertyFact label="Type">{property.estateType}</PropertyFact>
      </div>
      <div className="hidden lg:flex  justify-between ">
        <NumericPropertyFact label="Værelser" value={property.numberOfRooms} />
        <PropertyFact label="Sagsnr.">{property.caseNumber}</PropertyFact>
      </div>
    </div>
  );
};

// Landejendomme
// Landbrug | Lystejendom
const AgriculturalPropertyFacts: React.FC<HousePropertyFactProps> = ({
  property,
}) => {
  return (
    <div className="">
      <div className="flex justify-between ">
        <NumericPropertyFact
          label="Ejerudgifter (md.)"
          value={property.ownerExpensesTotalMonthly}
        />
        <BuiltYear
          yearBuilt={property.yearBuilt}
          yearRenovated={property.yearRenovationAddition}
        ></BuiltYear>
      </div>
      <div className="flex justify-between ">
        <NumericPropertyFact
          label="Boligareal"
          value={property.housingArea}
          isArea={true}
        />
        <EnergyLabelFact
          energyLabel={property.energyLabel}
          energyLabelBest={property.energyLabelBest}
          energyLabelWorst={property.energyLabelWorst}
        />
      </div>
      <div className="flex justify-between ">
        {property.plotArea > 0 ? (
          <NumericPropertyFact
            label="Grundareal"
            value={property.plotArea}
            isArea={true}
            useHectares={true}
          />
        ) : (
          <div className="flex h-6 w-80 justify-between"></div>
        )}
        <PropertyFact label="Type">{property.estateType}</PropertyFact>
      </div>
      <div className="flex justify-between ">
        <PropertyFact label="Andre bygnynger">-</PropertyFact>
        <PropertyFact label="Sagsnr.">{property.caseNumber}</PropertyFact>
      </div>
    </div>
  );
};

// Andelsboliger
const AndelsPropertyFacts: React.FC<HousePropertyFactProps> = ({
  property,
}) => {
  return (
    <div className="">
      <div className="flex justify-between ">
        <NumericPropertyFact
          label="Boligydelse (md.)"
          value={property.coopChargeMonthly}
        />
        <BuiltYear
          yearBuilt={property.yearBuilt}
          yearRenovated={property.yearRenovationAddition}
        ></BuiltYear>
      </div>
      <div className="flex justify-between ">
        <PropertyFact label="Varmeudgift (md.)">-</PropertyFact>
        <EnergyLabelFact
          energyLabel={property.energyLabel}
          energyLabelBest={property.energyLabelBest}
          energyLabelWorst={property.energyLabelWorst}
        />
      </div>
      <div className="flex justify-between ">
        <NumericPropertyFact
          label="Boligareal"
          value={property.housingArea}
          isArea={true}
        />
        <PropertyFact label="Type">{property.estateType}</PropertyFact>
      </div>
      <div className="flex justify-between ">
        <NumericPropertyFact label="Værelser" value={property.numberOfRooms} />
        <PropertyFact label="Sagsnr.">{property.caseNumber}</PropertyFact>
      </div>
    </div>
  );
};

// Grunde
const PlotOfLandPropertyFacts: React.FC<HousePropertyFactProps> = ({
  property,
}) => {
  return (
    <div className="">
      <div className="flex justify-between ">
        <NumericPropertyFact
          label="Ejerudgifter (md.)"
          value={property.ownerExpensesTotalMonthly}
        />
        <PropertyFact label="Type">{property.estateType}</PropertyFact>
      </div>
      <div className="flex justify-between ">
        {property.plotArea > 0 ? (
          <NumericPropertyFact
            label="Grundareal"
            value={property.plotArea}
            isArea={true}
          />
        ) : (
          <div className="flex h-6 w-80 justify-between"></div>
        )}
        <PropertyFact label="Sagsnr.">{property.caseNumber}</PropertyFact>
      </div>
      <div className="flex justify-between ">
        <div className="flex h-6 w-80 justify-between"></div>
        <div className="flex h-6 w-80 justify-between"></div>
      </div>
      <div className="flex justify-between ">
        <div className="flex h-6 w-80 justify-between"></div>
        <div className="flex h-6 w-80 justify-between"></div>
      </div>
    </div>
  );
};

// Lejebolig
const RentalPropertyFacts: React.FC<HousePropertyFactProps> = ({
  property,
}) => {
  return (
    <div className="">
      <div className="flex justify-between ">
        <NumericPropertyFact
          label="A/C-forbrug"
          value={property.consumptionCostsMonthly}
        />
        <BuiltYear
          yearBuilt={property.yearBuilt}
          yearRenovated={property.yearRenovationAddition}
        ></BuiltYear>
      </div>
      <div className="flex justify-between ">
        <NumericPropertyFact label="Depositum" value={property.deposit} />
        <NumericPropertyFact
          label="Forudbetalt leje"
          value={property.prepaidRent}
        />
      </div>
      <div className="flex justify-between ">
        <NumericPropertyFact
          label="Boligareal"
          value={property.housingArea}
          isArea={true}
        />
        <PropertyFact label="Type">{property.estateType}</PropertyFact>
      </div>
      <div className="flex justify-between ">
        <NumericPropertyFact label="Værelser" value={property.numberOfRooms} />
        <PropertyFact label="Sagsnr.">{property.caseNumber}</PropertyFact>
      </div>
    </div>
  );
};

// Erhverv | Investering
const CommercialInvestmentPropertyFacts: React.FC<HousePropertyFactProps> = ({
  property,
}) => {
  return (
    <div className="">
      <div className="hidden lg:flex justify-between ">
        <NumericPropertyFact
          label="Driftsudgifter"
          value={property.operatingCostsAnnually}
        />
        <BuiltYear
          yearBuilt={property.yearBuilt}
          yearRenovated={property.yearRenovationAddition}
        ></BuiltYear>
      </div>
      <div className="hidden lg:flex  justify-between ">
        <NumericPropertyFact
          label="Lejeindtægter"
          value={property.rentalIncomeAnnually}
        />
        <EnergyLabelFact
          energyLabel={property.energyLabel}
          energyLabelBest={property.energyLabelBest}
          energyLabelWorst={property.energyLabelWorst}
        />
      </div>
      <div className="hidden lg:flex  justify-between ">
        <NumericPropertyFact label="Afkast i %" value={property.rateOfReturn} />
        <PropertyFact label="Type">{property.estateType}</PropertyFact>
      </div>
      <div className="flex  justify-between ">
        <NumericPropertyFact
          label="Etageareal"
          value={property.floorArea}
          isArea={true}
        />
        <PropertyFact label="Sagsnr.">{property.caseNumber}</PropertyFact>
      </div>
    </div>
  );
};

// Erhverv | Eget brug (Køb)
const CommercialOwnUseBoughtPropertyFacts: React.FC<HousePropertyFactProps> = ({
  property,
}) => {
  return (
    <div className="">
      <div className="hidden lg:flex  justify-between ">
        <NumericPropertyFact
          label="Driftsudgifter"
          value={property.operatingCostsAnnually}
        />
        <BuiltYear
          yearBuilt={property.yearBuilt}
          yearRenovated={property.yearRenovationAddition}
        ></BuiltYear>
      </div>
      <div className="hidden lg:flex  justify-between ">
        <NumericPropertyFact
          label="Lejeindtægter"
          value={property.rentalIncomeAnnually}
        />
        <EnergyLabelFact
          energyLabel={property.energyLabel}
          energyLabelBest={property.energyLabelBest}
          energyLabelWorst={property.energyLabelWorst}
        />
      </div>
      <div className="flex  justify-between ">
        <NumericPropertyFact
          label="Etageareal"
          value={property.floorArea}
          isArea={true}
        />
        <PropertyFact label="Type">{property.estateType}</PropertyFact>
      </div>
      <div className="hidden lg:flex  justify-between ">
        {property.plotArea > 0 ? (
          <NumericPropertyFact
            label="Grundareal"
            value={property.plotArea}
            isArea={true}
            useHectares={false}
          />
        ) : (
          <div className="flex h-6 w-80 justify-between"></div>
        )}
        <PropertyFact label="Sagsnr.">{property.caseNumber}</PropertyFact>
      </div>
    </div>
  );
};

// Erhverv | Eget brug (Leje)
const CommercialOwnUseRentalPropertyFacts: React.FC<HousePropertyFactProps> = ({
  property,
}) => {
  return (
    <div className="">
      <div className="hidden lg:flex  justify-between ">
        <NumericPropertyFact
          label="Driftsudgifter"
          value={property.operatingCostsAnnually}
        />
        <BuiltYear
          yearBuilt={property.yearBuilt}
          yearRenovated={property.yearRenovationAddition}
        ></BuiltYear>
      </div>
      <div className="hidden lg:flex  justify-between ">
        s
        <NumericPropertyFact label="Depositum" value={property.deposit} />
        <EnergyLabelFact
          energyLabel={property.energyLabel}
          energyLabelBest={property.energyLabelBest}
          energyLabelWorst={property.energyLabelWorst}
        />
      </div>
      <div className="hidden lg:flex  justify-between ">
        <NumericPropertyFact
          label="Fællesudgifter"
          value={property.commonCostsAnnually}
        />
        <PropertyFact label="Type">{property.estateType}</PropertyFact>
      </div>
      <div className="hidden lg:flex  justify-between ">
        <NumericPropertyFact
          label="Etageareal"
          value={property.floorArea}
          isArea={true}
        />
        <PropertyFact label="Sagsnr.">{property.caseNumber}</PropertyFact>
      </div>
    </div>
  );
};

interface NumericPropertyFactProps {
  label: string;
  value: number;
  isArea?: boolean;
  useHectares?: boolean;
}

const NumericPropertyFact: React.FC<NumericPropertyFactProps> = ({
  label,
  value,
  isArea,
  useHectares,
}) => {
  return (
    <div className="flex h-6 w-80 justify-between">
      <span className="block ">{label}</span>
      <span className="block text-right ">
        {value > 0 ? (
          <span>
            {getValue(value, useHectares)} {getUnit(isArea, useHectares)}
          </span>
        ) : (
          <span>-</span>
        )}
      </span>
    </div>
  );
};

function getValue(value: number, useHectares?: boolean) {
  if (useHectares) {
    return squareMetersToHectares(value);
  }
  return formatNumber(value);
}

function getUnit(isArea?: boolean, useHectares?: boolean) {
  if (isArea) {
    if (useHectares) {
      return <span>ha</span>;
    }
    return (
      <span>
        m<sup>2</sup>
      </span>
    );
  }

  return <span></span>;
}

interface PropertyFactProps {
  label: string;
  children: React.ReactNode;
}

const PropertyFact: React.FC<PropertyFactProps> = ({ label, children }) => {
  return (
    <div className="flex h-6 w-80 justify-between">
      <span className="block ">{label}</span>
      <span className="block text-right ">{children}</span>
    </div>
  );
};

interface EnergyLabelFact {
  energyLabel: string;
  energyLabelBest?: string;
  energyLabelWorst?: string;
}

const EnergyLabelFact: React.FC<EnergyLabelFact> = ({
  energyLabel,
  energyLabelBest,
  energyLabelWorst,
}) => {
  return (
    <div className="flex h-6 w-80 justify-between">
      <span className="">Energimærke</span>
      <div className="flex items-center">
        {energyLabel ? (
          <EnergyLabelRange
            energyLabel={energyLabel}
            energyLabelBest={energyLabelBest}
            energyLabelWorst={energyLabelWorst}
          />
        ) : null}
      </div>
    </div>
  );
};

interface BuiltYearProps {
  yearBuilt: number;
  yearRenovated: number;
}

const BuiltYear: React.FC<BuiltYearProps> = ({ yearBuilt, yearRenovated }) => {
  return (
    <div className="hidden lg:flex  h-6 w-80 justify-between">
      <span className="block ">Byggeår</span>
      <span className="block text-right ">
        {yearBuilt > 0 ? (
          <span>
            {yearBuilt} {yearRenovated > 0 ? ` / ${yearRenovated}` : null}{" "}
          </span>
        ) : (
          <span>-</span>
        )}
      </span>
    </div>
  );
};
